import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  Wrench,
  Lock,
  CheckCircle,
  Package,
  Cog,
  Settings,
  FileCheck,
} from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#e6edf5] min-h-screen p-8">

      <div className="grid grid-cols-12 gap-6">

        {/* Raise Service Request */}

        <div className="col-span-7 bg-white rounded-xl shadow-md p-6 flex items-center gap-6">

          <div className="bg-blue-100 p-5 rounded-lg shadow">
            <ClipboardList className="text-blue-700" size={42} />
          </div>

          <div
            onClick={() => navigate("/create-ticket")}
            className="cursor-pointer"
          >
            <h2 className="text-xl font-semibold text-slate-700">
              Raise Service Request
            </h2>

            <p className="text-slate-500 text-sm">
              Submit a new request
            </p>
          </div>

        </div>

        {/* Track Your Tickets */}

        <div className="col-span-5 bg-white rounded-xl shadow-md p-6">

          <h3 className="font-semibold text-slate-700 mb-4">
            Track Your Tickets
          </h3>

          <div className="grid grid-cols-3 gap-3">

            <div className="bg-green-500 text-white rounded-lg p-4 flex justify-between items-center">
              <div>
                <p className="text-xs">Open Tickets</p>
                <Lock size={16} />
              </div>
              <span className="text-2xl font-bold">3</span>
            </div>

            <div className="bg-orange-500 text-white rounded-lg p-4 flex justify-between items-center">
              <div>
                <p className="text-xs">In Progress</p>
                <Settings size={16} />
              </div>
              <span className="text-2xl font-bold">2</span>
            </div>

            <div className="bg-blue-600 text-white rounded-lg p-4 flex justify-between items-center">
              <div>
                <p className="text-xs">Completed</p>
                <CheckCircle size={16} />
              </div>
              <span className="text-2xl font-bold">12</span>
            </div>



          </div>

        </div>

        {/* Recent Service Requests */}

        <div className="col-span-7 bg-white rounded-xl shadow-md p-6">

          <h3 className="font-semibold text-slate-700 mb-4">
            Recent Service Requests
          </h3>

          <table className="w-full text-sm">

            <thead className="bg-gray-100 text-slate-600">
              <tr>
                <th className="text-left p-3">Ticket ID</th>
                <th className="text-left">Subject</th>
                <th>Status</th>
                <th>Last Update</th>
              </tr>
            </thead>

            <tbody>

              <tr className="border-b">
                <td className="p-3 text-blue-600 font-medium">#10235</td>
                <td>AC Unit Repair</td>
                <td>
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                    In Progress
                  </span>
                </td>
                <td>March 09, 2024</td>
              </tr>

              <tr className="border-b">
                <td className="p-3 text-blue-600 font-medium">#10222</td>
                <td>Chiller Maintenance</td>
                <td>
                  <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded">
                    Open
                  </span>
                </td>
                <td>March 05, 2024</td>
              </tr>

              <tr>
                <td className="p-3 text-blue-600 font-medium">#10198</td>
                <td>Routine Inspection</td>
                <td>
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Completed
                  </span>
                </td>
                <td>March 02, 2024</td>
              </tr>

            </tbody>

          </table>

          <div className="flex justify-end mt-4">
            <button
              onClick={() => navigate("/tickets")}
              className="text-blue-600 text-sm font-medium"
            >
              View All →
            </button>
          </div>

        </div>

        {/* AMC & Warranty */}

        <div className="col-span-5 bg-white rounded-xl shadow-md p-6">

          <h3 className="font-semibold text-slate-700 mb-4">
            AMC & Warranty Status
          </h3>

          <div className="bg-green-200 rounded-lg p-4 mb-4">

            <p className="font-medium text-green-800">
              Active AMC Contract
            </p>

            <p className="text-sm text-green-900">
              Valid Until: Dec 31, 2024
            </p>

          </div>

          <div className="bg-gray-100 rounded-lg p-4">

            <p className="font-medium text-slate-700">
              Warranty Info
            </p>

            <p className="text-sm text-slate-600 mb-3">
              Expires: Aug 15, 2023
            </p>

            <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm">
              View Details
            </button>

          </div>

        </div>

        {/* Service History */}

        <div className="col-span-7 bg-white rounded-xl shadow-md p-6">

          <h3 className="font-semibold text-slate-700 mb-4">
            Service History
          </h3>

          <div className="grid grid-cols-4 text-center">

            <div className="flex flex-col items-center gap-2">
              <Package size={32} className="text-slate-500" />
              <p className="text-sm text-slate-500">Total Services</p>
              <p className="text-lg font-semibold">28</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Cog size={32} className="text-slate-500" />
              <p className="text-sm text-slate-500">Repairs</p>
              <p className="text-lg font-semibold">10</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Wrench size={32} className="text-slate-500" />
              <p className="text-sm text-slate-500">Maintenance</p>
              <p className="text-lg font-semibold">14</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <ClipboardList size={32} className="text-slate-500" />
              <p className="text-sm text-slate-500">Inspections</p>
              <p className="text-lg font-semibold">4</p>
            </div>

          </div>

        </div>

        {/* Announcements */}

        <div className="col-span-5 bg-white rounded-xl shadow-md p-6">

          <h3 className="font-semibold text-slate-700 mb-4">
            Announcements
          </h3>

          <ul className="text-sm text-slate-600 space-y-2">

            <li>
              • <strong>Scheduled Maintenance:</strong> System downtime on May 5th
            </li>

            <li>
              • <strong>Holiday Notice:</strong> Office closed on May 25th
            </li>

          </ul>

          <button className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md text-sm">
            Read More
          </button>

        </div>

      </div>

    </div>
  );
};

export default Dashboard;